import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage('No verification token found. Please check your email for the verification link.');
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      setStatus('verifying');
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        
        // Store token and set auth state
        if (data.token) {
          localStorage.setItem('token', data.token);
          if (setAuthUser && data.user) {
            setAuthUser(data.user, data.token);
          }
        }
        
        // Redirect to home after 2 seconds
        setTimeout(() => navigate('/home'), 2000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Verification failed');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  const handleResend = async () => {
    if (!resendEmail) return;
    
    try {
      setResendStatus('sending');
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail })
      });
      const data = await response.json();
      setResendStatus(data.success ? 'sent' : 'error');
    } catch (err) {
      setResendStatus('error');
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0d47a1 0%, #1a73e8 50%, #42a5f5 100%)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: '20px'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
  };

  const iconSize = { fontSize: '64px', marginBottom: '16px' };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {status === 'verifying' && (
          <>
            <div style={iconSize}>⏳</div>
            <h2 style={{ color: '#1a1a1a', marginBottom: '12px' }}>Verifying your email...</h2>
            <p style={{ color: '#666' }}>Please wait while we confirm your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={iconSize}>✅</div>
            <h2 style={{ color: '#2e7d32', marginBottom: '12px' }}>Email Verified!</h2>
            <p style={{ color: '#666' }}>{message}</p>
            <p style={{ color: '#888', fontSize: '14px', marginTop: '16px' }}>Redirecting to your feed...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={iconSize}>❌</div>
            <h2 style={{ color: '#c62828', marginBottom: '12px' }}>Verification Failed</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{message}</p>
            <div style={{ textAlign: 'left' }}>
              <label style={{ display: 'block', color: '#555', marginBottom: '8px', fontSize: '14px' }}>
                Enter your email to get a new verification link:
              </label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  width: '100%', padding: '12px', borderRadius: '8px',
                  border: '1px solid #ddd', fontSize: '14px', marginBottom: '12px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={handleResend}
                disabled={resendStatus === 'sending'}
                style={{
                  width: '100%', padding: '12px', borderRadius: '8px',
                  background: '#1a73e8', color: '#fff', border: 'none',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                {resendStatus === 'sending' ? 'Sending...' : 
                 resendStatus === 'sent' ? '✓ Sent! Check your email' : 
                 'Resend Verification Email'}
              </button>
            </div>
            <p style={{ marginTop: '24px' }}>
              <a href="/login" style={{ color: '#1a73e8', textDecoration: 'none' }}>← Back to Login</a>
            </p>
          </>
        )}

        {status === 'no-token' && (
          <>
            <div style={iconSize}>📧</div>
            <h2 style={{ color: '#1a1a1a', marginBottom: '12px' }}>Check Your Email</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{message}</p>
            <p>
              <a href="/login" style={{ color: '#1a73e8', textDecoration: 'none' }}>← Back to Login</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
